import type { Router } from 'vue-router';
import {
  safeLocalStorageGetItem,
  safeLocalStorageSetItem,
} from '@/utils/safeLocalStorage';

/** @internal exported for unit tests */
export const CHECK_COOLDOWN_MS = 30 * 60 * 1000;
/** @internal exported for unit tests */
export const VISIBLE_DELAY_MS = 5000;
const LAST_CHECK_KEY = 'indmo-sw-last-update-check-at';

let initialized = false;
let upgradePending = false;
let updateInFlight = false;
let visibleDelayTimer: ReturnType<typeof setTimeout> | null = null;
let hadControllerAtInit = false;

/** @internal exported for unit tests */
export function parseLastCheckAt(raw: string | null): number | null {
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** @internal exported for unit tests */
export function readLastCheckAtMs(getItem: (key: string) => string | null): number | null {
  try {
    return parseLastCheckAt(getItem(LAST_CHECK_KEY));
  } catch {
    return null;
  }
}

/** @internal exported for unit tests */
export function shouldSkipUpdateCheck(
  nowMs: number,
  lastCheckAtMs: number | null,
  cooldownMs: number
): boolean {
  if (lastCheckAtMs == null) return false;
  return nowMs - lastCheckAtMs < cooldownMs;
}

function writeLastCheckAt(nowMs: number): void {
  safeLocalStorageSetItem(LAST_CHECK_KEY, String(nowMs));
}

function storedLastCheckAtMs(): number | null {
  return readLastCheckAtMs(safeLocalStorageGetItem);
}

function logUpdateCheckSkippedCooldown(label: string, nowMs: number, lastCheckAtMs: number | null): void {
  if (lastCheckAtMs == null) return;
  const waitMs = CHECK_COOLDOWN_MS - (nowMs - lastCheckAtMs);
  const sec = Math.max(0, Math.round(waitMs / 1000));
  console.log('[swUpdateOrchestrator] update check skipped (cooldown):', label, `${sec}s remaining`);
}

function markUpgradePending(): void {
  upgradePending = true;
}

/** @internal exported for unit tests */
export function isChunkLoadErrorCandidate(reason: unknown): boolean {
  if (reason instanceof Error) {
    const m = reason.message || '';
    if (
      m.includes('Failed to fetch dynamically imported module') ||
      m.includes('error loading dynamically imported module') ||
      m.includes('Importing a module script failed') ||
      m.includes('ChunkLoadError')
    ) {
      return true;
    }
  }
  if (typeof reason === 'string') {
    const s = reason;
    if (
      s.includes('Failed to fetch dynamically imported module') ||
      s.includes('Importing a module script failed') ||
      s.includes('ChunkLoadError')
    ) {
      return true;
    }
  }
  // Duck-type browser `ErrorEvent` / `Event` (Node unit tests have no `ErrorEvent` global).
  if (typeof reason === 'object' && reason !== null) {
    const r = reason as { message?: unknown; error?: unknown; target?: unknown };
    if (typeof r.message === 'string' && r.message && isChunkLoadErrorCandidate(r.message)) {
      return true;
    }
    if (r.error !== undefined && isChunkLoadErrorCandidate(r.error)) {
      return true;
    }
    const t = r.target;
    if (typeof t === 'object' && t !== null && 'src' in t && typeof (t as { src?: unknown }).src === 'string') {
      const src = (t as { src: string }).src;
      try {
        const base =
          typeof window !== 'undefined' && window.location?.origin
            ? window.location.origin
            : 'http://localhost';
        const u = new URL(src, base);
        if (u.pathname.includes('/assets/')) return true;
      } catch {
        /* ignore */
      }
    }
  }
  return false;
}

function onWindowError(ev: Event): void {
  if (isChunkLoadErrorCandidate(ev)) {
    window.location.reload();
  }
}

function onUnhandledRejection(ev: PromiseRejectionEvent): void {
  if (isChunkLoadErrorCandidate(ev.reason)) {
    window.location.reload();
  }
}

async function performUpdateCheck(trigger: string): Promise<void> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  if (updateInFlight) return;
  const now = Date.now();
  const last = storedLastCheckAtMs();
  if (shouldSkipUpdateCheck(now, last, CHECK_COOLDOWN_MS)) {
    logUpdateCheckSkippedCooldown(trigger, now, last);
    return;
  }

  updateInFlight = true;
  try {
    console.log('[swUpdateOrchestrator] service worker update check:', trigger);
    const reg = await navigator.serviceWorker.getRegistration();
    await reg?.update();
  } catch {
    // ignore
  } finally {
    writeLastCheckAt(Date.now());
    updateInFlight = false;
  }
}

function clearVisibleDelayTimer(): void {
  if (visibleDelayTimer != null) {
    clearTimeout(visibleDelayTimer);
    visibleDelayTimer = null;
  }
}

/** Cooldown checked immediately; 5s delay only staggers `registration.update()` after resume. */
function scheduleVisibleUpdateCheck(): void {
  clearVisibleDelayTimer();
  const now = Date.now();
  const last = storedLastCheckAtMs();
  if (shouldSkipUpdateCheck(now, last, CHECK_COOLDOWN_MS)) {
    logUpdateCheckSkippedCooldown('visibility', now, last);
    return;
  }
  visibleDelayTimer = setTimeout(() => {
    visibleDelayTimer = null;
    void performUpdateCheck('visible-delay');
  }, VISIBLE_DELAY_MS);
}

function onVisibilityChange(): void {
  const state = document.visibilityState;
  if (state === 'hidden') {
    clearVisibleDelayTimer();
    if (upgradePending) {
      window.location.reload();
    }
    return;
  }
  if (state === 'visible') {
    scheduleVisibleUpdateCheck();
  }
}

function onControllerChange(): void {
  if (hadControllerAtInit) {
    markUpgradePending();
  }
  hadControllerAtInit = true;
}

/**
 * Production-only: hourly-gated `registration.update()`, post-visibility delayed check,
 * and silent full reload when a new SW takes control (tab hidden or next navigation) plus
 * chunk-load error recovery.
 */
export function initSwUpdateOrchestrator(router: Router): void {
  if (!import.meta.env.PROD) return;
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (initialized) return;
  initialized = true;

  if (!('serviceWorker' in navigator)) {
    window.addEventListener('error', onWindowError);
    window.addEventListener('unhandledrejection', onUnhandledRejection);
    return;
  }

  hadControllerAtInit = !!navigator.serviceWorker.controller;

  navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);
  document.addEventListener('visibilitychange', onVisibilityChange);

  window.addEventListener('error', onWindowError);
  window.addEventListener('unhandledrejection', onUnhandledRejection);

  router.beforeEach((to) => {
    if (!upgradePending) return true;
    const path = to.fullPath || to.path;
    window.location.assign(path);
    return false;
  });

  // Initial load: one path only (`startup`). Post-visibility delayed checks run from
  // `visibilitychange` → visible when the user returns from another tab/app — not here, or every
  // refresh would duplicate cooldown evaluation with `startup`.
  void performUpdateCheck('startup');
}
